import React from 'react';
import {
  Box,
  Button,
  ListSubheader,
  Select,
  Switch,
  MenuItem,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Text } from '@aws-amplify/ui-react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MessageEditor from './MessageEditor';
import { camelCaseToTitleCase } from '../utils/stringConversion';

interface SMSTriggerProps {
  index: number;
  trigger: any;
  setFeaturesList: (callback: (prev: any) => any) => void;
  featuresList: any;
  smsVariables: any[];
}

export default function SMSTrigger({
  index,
  trigger,
  setFeaturesList,
  featuresList,
  smsVariables,
}: SMSTriggerProps) {
  // Validate smsVariables is a non-empty array
  const hasSmsVariables =
    Array.isArray(smsVariables) && smsVariables.length > 0;

  const targetOptions = [
    { key: 'customer', label: 'Customer' },
    { key: 'closer', label: 'Closer' },
  ];

  const notificationTypes = [
    {
      key: 'reminder',
      label: 'Reminder',
    },
    {
      key: 'statusChange',
      label: 'Status Change',
    },
  ];

  const appointmentStatuses = [
    {
      key: 'scheduled',
      label: 'Scheduled',
    },
    {
      key: 'rescheduled',
      label: 'Rescheduled',
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
    },
  ];

  // Handler for message changes from MessageEditor
  const handleMessageChange = (newMessage: string) => {
    const updatedTriggers = [
      ...featuresList.schedule.appointmentNotifications.triggers,
    ];
    updatedTriggers[index] = {
      ...updatedTriggers[index],
      message: newMessage,
    };

    setFeaturesList((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        appointmentNotifications: {
          ...prev.schedule.appointmentNotifications,
          triggers: updatedTriggers,
        },
      },
    }));
  };

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: 'lightgray',
          borderRadius: '4px',
        }}
      >
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
            overflow: 'hidden',
          }}
        >
          <Text
            style={{
              maxWidth: '90%',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              width: '100%',
            }}
          >
            {trigger.enabled ? '✓' : '✗'}{' '}
            {trigger.target === 'closer' ? 'Closer' : 'Customer'}{' '}
            {trigger.type.charAt(0).toUpperCase() + trigger.type.slice(1)} -{' '}
            {trigger.message}
          </Text>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            width: '100%',
          }}
        >
          <Box
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'start',
              justifyContent: 'space-between',
            }}
          >
            <Text style={{ fontWeight: 'bold' }}>Enabled</Text>
            <Switch
              checked={trigger.enabled}
              onChange={() => {
                const updatedTriggers = [
                  ...featuresList.schedule.appointmentNotifications.triggers,
                ];
                updatedTriggers[index] = {
                  ...updatedTriggers[index],
                  enabled: !updatedTriggers[index].enabled,
                };

                setFeaturesList((prev) => ({
                  ...prev,
                  schedule: {
                    ...prev.schedule,
                    appointmentNotifications: {
                      ...prev.schedule.appointmentNotifications,
                      triggers: updatedTriggers,
                    },
                  },
                }));
              }}
            />
          </Box>
          <Box style={{ display: 'flex', gap: '10px' }}>
            <Box style={{ flex: 1 }}>
              <Text style={{ fontWeight: 'bold' }}>Target</Text>
              <Select
                fullWidth
                value={trigger.target === 'closer' ? 'closer' : 'customer'}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      backgroundColor: 'white',
                    },
                  },
                }}
                onChange={(e) => {
                  const updatedTriggers = [
                    ...featuresList.schedule.appointmentNotifications.triggers,
                  ];
                  updatedTriggers[index] = {
                    ...updatedTriggers[index],
                    target: e.target.value === 'closer' ? 'closer' : '',
                  };

                  setFeaturesList((prev) => ({
                    ...prev,
                    schedule: {
                      ...prev.schedule,
                      appointmentNotifications: {
                        ...prev.schedule.appointmentNotifications,
                        triggers: updatedTriggers,
                      },
                    },
                  }));
                }}
              >
                {targetOptions.map((option) => (
                  <MenuItem key={`target-${option.key}`} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            {trigger.target !== 'closer' && (
              <Box style={{ flex: 1 }}>
                <Text style={{ fontWeight: 'bold' }}>Phone Number Field</Text>
                <Select
                  fullWidth
                  value={
                    trigger.target && trigger.target !== 'closer'
                      ? trigger.target
                      : ''
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        backgroundColor: 'white',
                      },
                    },
                  }}
                  onChange={(e) => {
                    const updatedTriggers = [
                      ...featuresList.schedule.appointmentNotifications
                        .triggers,
                    ];
                    updatedTriggers[index] = {
                      ...updatedTriggers[index],
                      target: e.target.value,
                    };

                    setFeaturesList((prev) => ({
                      ...prev,
                      schedule: {
                        ...prev.schedule,
                        appointmentNotifications: {
                          ...prev.schedule.appointmentNotifications,
                          triggers: updatedTriggers,
                        },
                      },
                    }));
                  }}
                >
                  {hasSmsVariables ? (
                    smsVariables.map((variable, varIndex) => {
                      if (variable.type === 'Contact') {
                        return [
                          <ListSubheader
                            key={`sms-header-${variable.id || varIndex}`}
                          >
                            {variable.heading}
                          </ListSubheader>,
                          ...Object.keys(variable.contactFields)
                            .filter((key) => variable.contactFields[key].show)
                            .map((key, idx) => (
                              <MenuItem
                                key={`sms-field-${
                                  variable.id || varIndex
                                }-${key}-${idx}`}
                                value={variable.name + '.' + key}
                                style={{
                                  paddingLeft: '36px',
                                }}
                              >
                                {camelCaseToTitleCase(key)}
                              </MenuItem>
                            )),
                        ];
                      }
                      return (
                        <MenuItem
                          key={`sms-var-${
                            variable.id || `${varIndex}-${variable.name}`
                          }`}
                          value={variable.name}
                        >
                          {variable.label}
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled value="">
                      Loading options...
                    </MenuItem>
                  )}
                </Select>
              </Box>
            )}
          </Box>

          <Box style={{ display: 'flex', gap: '10px' }}>
            <Box style={{ flex: 1 }}>
              <Text style={{ fontWeight: 'bold' }}>Type</Text>
              <Select
                fullWidth
                value={trigger.type || 'reminder'}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      backgroundColor: 'white',
                    },
                  },
                }}
                onChange={(e) => {
                  const updatedTriggers = [
                    ...featuresList.schedule.appointmentNotifications.triggers,
                  ];
                  updatedTriggers[index] = {
                    ...updatedTriggers[index],
                    type: e.target.value,
                  };
                  if (e.target.value === 'reminder') {
                    delete updatedTriggers[index].minutesBefore;
                  } else delete updatedTriggers[index].status;

                  setFeaturesList((prev) => ({
                    ...prev,
                    schedule: {
                      ...prev.schedule,
                      appointmentNotifications: {
                        ...prev.schedule.appointmentNotifications,
                        triggers: updatedTriggers,
                      },
                    },
                  }));
                }}
              >
                {notificationTypes.map((option) => (
                  <MenuItem
                    key={`notification-${option.key}`}
                    value={option.key}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {trigger.type === 'reminder' && (
              <Box style={{ flex: 1 }}>
                <Text style={{ fontWeight: 'bold' }}>
                  Send Reminder (minutes before)
                </Text>
                <TextField
                  type="number"
                  fullWidth
                  disabled={trigger.type === 'on'}
                  value={trigger.minutesBefore}
                  onChange={(e) => {
                    const updatedTriggers = [
                      ...featuresList.schedule.appointmentNotifications
                        .triggers,
                    ];
                    updatedTriggers[index] = {
                      ...updatedTriggers[index],
                      minutesBefore: parseInt(e.target.value, 10),
                    };

                    setFeaturesList((prev) => ({
                      ...prev,
                      schedule: {
                        ...prev.schedule,
                        appointmentNotifications: {
                          ...prev.schedule.appointmentNotifications,
                          triggers: updatedTriggers,
                        },
                      },
                    }));
                  }}
                />
              </Box>
            )}
            {trigger.type === 'statusChange' && (
              <Box style={{ flex: 1 }}>
                <Text style={{ fontWeight: 'bold' }}>Status</Text>
                <Select
                  fullWidth
                  value={trigger.status || 'pending'}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        backgroundColor: 'white',
                      },
                    },
                  }}
                  onChange={(e) => {
                    const updatedTriggers = [
                      ...featuresList.schedule.appointmentNotifications
                        .triggers,
                    ];
                    updatedTriggers[index] = {
                      ...updatedTriggers[index],
                      status: e.target.value,
                    };

                    setFeaturesList((prev) => ({
                      ...prev,
                      schedule: {
                        ...prev.schedule,
                        appointmentNotifications: {
                          ...prev.schedule.appointmentNotifications,
                          triggers: updatedTriggers,
                        },
                      },
                    }));
                  }}
                >
                  {appointmentStatuses.map((option) => (
                    <MenuItem key={`status-${option.key}`} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}
          </Box>

          {/* Use the new MessageEditor component */}
          <MessageEditor
            message={trigger.message || ''}
            onChange={handleMessageChange}
            smsVariables={smsVariables}
          />

          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '10px',
            }}
          >
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                const updatedTriggers = [
                  ...featuresList.schedule.appointmentNotifications.triggers,
                ];
                updatedTriggers.splice(index, 1);

                setFeaturesList((prev) => ({
                  ...prev,
                  schedule: {
                    ...prev.schedule,
                    appointmentNotifications: {
                      ...prev.schedule.appointmentNotifications,
                      triggers: updatedTriggers,
                    },
                  },
                }));
              }}
            >
              Delete Trigger
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
